import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase';
import { normalizeUsername } from '../utils/profileDefaults';

function requireDb() {
  if (!db) {
    throw new Error('Firestore is not configured. Add REACT_APP_FIREBASE_* values to .env.local.');
  }
}

export function normalizeEmailKey(value) {
  return String(value || '').trim().toLowerCase();
}

export async function findUserByUsername(username) {
  requireDb();
  const usernameLower = normalizeUsername(username);
  if (!usernameLower) {
    return null;
  }

  const snapshot = await getDoc(doc(db, 'usernames', usernameLower));
  if (!snapshot.exists()) {
    return null;
  }
  return snapshot.data();
}

export async function findUserByEmail(email) {
  requireDb();
  const emailKey = normalizeEmailKey(email);
  if (!emailKey) {
    return null;
  }

  const snapshot = await getDoc(doc(db, 'emailLookups', emailKey));
  if (!snapshot.exists()) {
    return null;
  }
  return snapshot.data();
}

export async function findFriendTarget(value) {
  const input = String(value || '').trim();
  if (!input) {
    return null;
  }
  return input.includes('@') ? findUserByEmail(input) : findUserByUsername(input);
}

export async function createFriendRequest(senderProfile, target) {
  requireDb();
  if (!senderProfile?.ownerId) {
    throw new Error('Create your profile before sending friend requests.');
  }
  if (!target?.uid || target.uid === senderProfile.ownerId) {
    throw new Error('Choose another user to add as a friend.');
  }

  const now = serverTimestamp();
  const ref = await addDoc(collection(db, 'friendRequests'), {
    fromUid: senderProfile.ownerId,
    fromUsername: senderProfile.username || '',
    fromDisplayName: senderProfile.displayName || '',
    toUid: target.uid,
    toUsername: target.username || '',
    toDisplayName: target.displayName || '',
    status: 'pending',
    createdAt: now,
    updatedAt: now,
  });
  return ref.id;
}

export async function listIncomingFriendRequests(uid) {
  requireDb();
  const snapshot = await getDocs(query(
    collection(db, 'friendRequests'),
    where('toUid', '==', uid),
    where('status', '==', 'pending')
  ));
  return snapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() }));
}

export async function listOutgoingFriendRequests(uid) {
  requireDb();
  const snapshot = await getDocs(query(
    collection(db, 'friendRequests'),
    where('fromUid', '==', uid),
    where('status', '==', 'pending')
  ));
  return snapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() }));
}

export async function listFriends(uid) {
  requireDb();
  const snapshot = await getDocs(collection(db, 'profileFriends', uid, 'friends'));
  return snapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() }));
}

export async function isAcceptedFriend(ownerId, viewerUid) {
  if (!ownerId || !viewerUid || ownerId === viewerUid) {
    return false;
  }

  requireDb();
  const snapshot = await getDoc(doc(db, 'profileFriends', ownerId, 'friends', viewerUid));
  return snapshot.exists();
}

export async function acceptFriendRequest(request) {
  requireDb();
  const now = serverTimestamp();
  await updateDoc(doc(db, 'friendRequests', request.id), {
    status: 'accepted',
    updatedAt: now,
  });

  const batch = writeBatch(db);
  batch.set(doc(db, 'profileFriends', request.fromUid, 'friends', request.toUid), {
    ownerId: request.fromUid,
    friendUid: request.toUid,
    requestId: request.id,
    createdAt: now,
    updatedAt: now,
  });
  batch.set(doc(db, 'profileFriends', request.toUid, 'friends', request.fromUid), {
    ownerId: request.toUid,
    friendUid: request.fromUid,
    requestId: request.id,
    createdAt: now,
    updatedAt: now,
  });
  await batch.commit();
}

export async function declineFriendRequest(requestId) {
  requireDb();
  await updateDoc(doc(db, 'friendRequests', requestId), {
    status: 'declined',
    updatedAt: serverTimestamp(),
  });
}

export async function cancelFriendRequest(requestId) {
  requireDb();
  await updateDoc(doc(db, 'friendRequests', requestId), {
    status: 'cancelled',
    updatedAt: serverTimestamp(),
  });
}
